import { store } from "@store/store"; // Get rid of this if useSelector works better ( along with getBackdoorTableOptions below)
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING, SAVING } from "@constants";
import {
  getFromServerTopic,
  getFromServerTopic2,
  getFromServerPublicTopic,
} from "@utility/slice";
import { NumberRangeColumnFilter } from "@helpers/tablefilters";

const SLICE = `curtailment`;

const action = (name) => {
  return `${SLICE}/${name}`;
};

// Get Rid of this if useSelector works better (along with store import above)
export const getBackdoorTableOptions = (tableName) => {
  return {
    ...(store.getState().curtailment.tableOpts?.[tableName] ?? {
      filters: [],
      sortBy: [],
    }),
  };
};

// Capture Groups are $1: Date yyyy-mm-dd, $2: Time hh:mm or hh:mm:ss, $3: Milliseconds .vvv
export const reDateTime =
  /^([0-9]{4}-[0-9]{2}-[0-9]{2})[ T]?([0-9]{2}:[0-9]{2}(?::[0-9]{2})?)?(\.[0-9]{3})?Z?$/;

function resolveDate(value) {
  return value ? value?.replace(reDateTime, "$1T$2") : value;
}

export const formatDateTime = function (
  format = "Y-m-d H:i:s",
  timestamp = null,
) {
  const dt = timestamp === null ? new Date() : new Date(timestamp);
  format = format ? format : "Y-m-d H:i:s";
  return format
    .replace(/Y/g, dt.getFullYear())
    .replace(/m/g, `00${dt.getMonth() + 1}`.slice(-2))
    .replace(/d/g, `00${dt.getDate()}`.slice(-2))
    .replace(/H/g, `00${dt.getHours()}`.slice(-2))
    .replace(/i/g, `00${dt.getMinutes()}`.slice(-2))
    .replace(/s/g, `00${dt.getSeconds()}`.slice(-2))
    .replace(/v/g, `000${dt.getMilliseconds()}`.slice(-3));
};

export const formatPhoneNumber = function (data) {
  const phoneRegEx = [
    { find: /^([\d]{3})([\d]{3})([\d]{4})$/, repl: "($1) $2-$3" },
    { find: /^([\d]{1})?([\d]{3})([\d]{3})([\d]{4})$/, repl: "+$1 ($2) $3-$4" },
  ];
  for (let i = 0; i < phoneRegEx.length; i++) {
    if (phoneRegEx[i].find.test(data)) {
      return data.replace(phoneRegEx[i].find, phoneRegEx[i].repl);
    }
  }
  return data;
};

export const compareTableOpts = (opts1, opts2) => {
  return JSON.stringify(opts1) === JSON.stringify(opts2);
};

/*
 * Declare functions used to generate other functions here so they are available for use later
 */

export const getFromServer = (action, payloadFn) => {
  return getFromServerTopic({ topic: "curtailment", action, payloadFn });
};

export const getFromServer2 = (name, payloadFn, thunkOptions) => {
  return getFromServerTopic2("curtailment", name, payloadFn, thunkOptions);
};

export const getFromServerPublic = (action, contextOptionsFn, payloadFn) =>
  getFromServerPublicTopic({
    topic: SLICE,
    action,
    contextOptionsFn,
    payloadFn,
  });

export const setEventEmailNotificationOpened = getFromServerPublic(
  "setEventEmailNotificationOpened",
  async ({ arg }) => arg,
  async (arg) => arg,
);

export const setEventEmailNotificationAcknowledged = getFromServerPublic(
  "setEventEmailNotificationAcknowledged",
  async ({ arg }) => arg,
  async (arg) => arg,
);

export const saveToServer = (methodName, payloadFn, thunkOptions) => {
  return getFromServerTopic2(
    "curtailment",
    methodName,
    payloadFn,
    thunkOptions,
  );
};

export const loadValidators = getFromServer2(
  "loadValidators",
  async (payload) => {
    return { binds: { formName: payload ?? null } };
  },
  {
    condition: (arg, { getState }) => {
      const wants = arg instanceof Array ? arg : [arg];
      const validators = getState().curtailment.validators;
      return (
        wants.filter(
          (name) =>
            !validators?.[name] || Object.keys(validators[name]).length === 0,
        ).length > 0
      );
    },
  },
);

const pendingRequest = (state, status) => {
  state.pendingRequests++;
  state.status = status;
};

const completedRequest = (state) => {
  state.pendingRequests--;

  if (state.pendingRequests < 1) {
    state.pendingRequests = 0;
    state.status = IDLE;
  }
};
const rejectedRequest = (state) => {
  state.pendingRequests--;

  if (state.pendingRequests < 1) {
    state.pendingRequests = 0;
    state.status = IDLE;
  }
};

export const prepForForm = (payload) => {
  if (!(payload instanceof Array)) {
    const obj = { ...payload };
    for (const p in obj) {
      obj[p] = obj[p] ?? undefined;
    }
    return obj;
  }
  return payload.map((item) => prepForForm(item));
};

export const prepForDatabase = (payload) => {
  if (!(payload instanceof Array)) {
    const obj = { ...payload };
    for (const p in obj) {
      obj[p] = obj[p] ?? null;
    }
    return obj;
  }
  return payload.map((item) => prepForDatabase(item));
};

const getAreaTypesForDropdown = getFromServer2(
  "getAreaTypesForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.areaTypes?.length === 0;
    },
  },
);
const getProgramAdminsForDropdown = getFromServer2(
  "getProgramAdminsForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.programAdmins?.length === 0;
    },
  },
);
const getProgramsForDropdown = getFromServer2(
  "getProgramsForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.programs?.length === 0;
    },
  },
);

const getEnergyTypesForDropdown = getFromServer2(
  "getEnergyTypesForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.energyTypes?.length === 0;
    },
  },
);
const getBaselineMethodsForDropdown = getFromServer2(
  "getBaselineMethodsForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.baselineMethods?.length === 0;
    },
  },
);

const getSitesForDropdown = getFromServer2("getSitesForDropdown", undefined, {
  condition: (arg, { getState }) => {
    const state = getState().curtailment;
    return state?.sites?.length === 0;
  },
});

const getOrganizationsForDropdown = getFromServer2(
  "getOrganizationsForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.organizations?.length === 0;
    },
  },
);

const getVoiceFilesForDropdown = getFromServer2(
  "getVoiceFilesForDropdown",
  undefined,
  {
    condition: (arg, { getState }) => {
      const state = getState().curtailment;
      return state?.voiceFiles?.length === 0;
    },
  },
);

const getZonesForDropdown = getFromServer2("getZonesForDropdown", undefined, {
  condition: (arg, { getState }) => {
    const state = getState().curtailment;
    return state?.zones?.length === 0;
  },
});

/*
Loading Full Tables into the database section of the slice
*/

export const readAllPrograms = getFromServer2("readAllPrograms", undefined, {
  condition: (arg, { getState }) => {
    const state = getState().curtailment;
    return state?.database?.programs?.length === 0;
  },
});

/*
 * Page Initialization routines. These are run when the associated page
 * is instantiated to ensure that the page has everything it needs to run
 */

export const initializeAreaPage = createAsyncThunk(
  action("initializeAreaPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getAreaTypesForDropdown()),
      dispatch(getProgramAdminsForDropdown()),
      dispatch(getProgramsForDropdown()),
      dispatch(loadValidators("area")),
    ]);
  },
);
export const initializeAreaTypePage = createAsyncThunk(
  action("initializeAreaTypePage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getAreaTypesForDropdown()),
      dispatch(loadValidators("areaType")),
    ]);
  },
);

export const initializeContactPage = createAsyncThunk(
  action("initializeContactPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getOrganizationsForDropdown()),
      dispatch(loadValidators("contact", "contactSite")),
    ]);
  },
);
export const initializeCurtailPlanPage = createAsyncThunk(
  action("initializeCurtailPlanPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getProgramsForDropdown()),
      dispatch(getSitesForDropdown()),
      dispatch(loadValidators("curtailPlan")),
    ]);
  },
);

export const initializeProgramPage = createAsyncThunk(
  action("initializeProgramPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getProgramAdminsForDropdown()),
      dispatch(getBaselineMethodsForDropdown()),
      dispatch(loadValidators("program")),
    ]);
  },
);

export const initializeEnrollmentRecordPage = createAsyncThunk(
  action("initializeEnrollmentRecordPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getSitesForDropdown()),
      dispatch(getProgramsForDropdown()),
      dispatch(loadValidators(["enrollmentRecord", "revenueShare"])),
    ]);
  },
);

export const initializeEventPage = createAsyncThunk(
  action("initializeEventPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getVoiceFilesForDropdown()),
      dispatch(getProgramsForDropdown()),
      dispatch(readAllPrograms()),
      dispatch(loadValidators(["event", "eventCommunication"])),
    ]);
  },
);

export const initializeMMAReportPage = createAsyncThunk(
  action("initializeMMAReportPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      dispatch(getProgramAdminsForDropdown()),
      dispatch(getOrganizationsForDropdown()),
      dispatch(getEnergyTypesForDropdown()),
      // dispatch(getAccountsForDropdown()),
      // dispatch(getResourcesForDropdown()),
      dispatch(loadValidators("mmaReportCriteria")),
    ]);
  },
);

export const initializeACLReportPage = createAsyncThunk(
  action("initializeACLReportPage"),
  async (arg, { dispatch }) => {
    return Promise.all([
      //dispatch(getZonesForDropdown()),
      dispatch(loadValidators("aclReportCriteria")),
    ]);
  },
);

export const initializeThingPage = createAsyncThunk(
  action("initializeThingPage"),
  async (arg, { dispatch }) => {
    return Promise.all([dispatch(getProgramsForDropdown())]);
  },
);

export const readAllThingsForTable = getFromServer("readAllThingsForTable");

export const readAllCurtailPlansForTable = getFromServer2(
  "readAllCurtailPlansForTable",
);
export const readAllAreasForTable = getFromServer2("readAllAreasForTable");
export const readAllAreaTypesForTable = getFromServer(
  "readAllAreaTypesForTable",
);
export const readAllBaselineMethodsForTable = getFromServer(
  "readAllBaselineMethodsForTable",
);
export const readAllContactsForTable = getFromServer2(
  "readAllContactsForTable",
);
export const readAllEnrollmentRecordsForTable = getFromServer2(
  "readAllEnrollmentRecordsForTable",
);

export const readAllEventEmailNotificationsForTable = getFromServer(
  "readAllEventEmailNotificationsForTable",
);
export const readAllEventEmailTemplatesForTable = getFromServer(
  "readAllEventEmailTemplatesForTable",
);
export const readAllEventEmailTypesForTable = getFromServer(
  "readAllEventEmailTypesForTable",
);
export const readAllEventsForTable = getFromServer2("readAllEventsForTable");
export const readAllProgramAdminsForTable = getFromServer(
  "readAllProgramAdminsForTable",
);
export const readAllProgramsForTable = getFromServer("readAllProgramsForTable");
export const readAllProgramPricesForTable = getFromServer(
  "readAllProgramPricesForTable",
);
export const readAllResourcesForTable = getFromServer(
  "readAllResourcesForTable",
);
export const readAllRevenueSharesForTable = getFromServer(
  "readAllRevenueSharesForTable",
);

export const prepareEventEmailCommunications = getFromServer2(
  "prepareEventEmailCommunications",
  async (payload) => {
    return { binds: { id: payload } };
  },
);

export const prepareEventTextCommunications = getFromServer2(
  "prepareEventTextCommunications",
  async (payload) => {
    return { binds: { id: payload } };
  },
);

export const prepareEventVoiceCommunications = getFromServer2(
  "prepareEventVoiceCommunications",
  async (payload) => {
    return { binds: { id: payload } };
  },
);

export const sendEventCommunications = getFromServer2(
  "sendEventCommunications",
  async (payload) => {
    return { binds: { id: payload } };
  },
);

export const cleanRecord = (formName) => {
  const layout = {
    area: {
      id: null,
      programId: null,
      areaTypeId: null,
      name: null,
      subdivision: null,
      callWindowStart: null,
      callWindowEnd: null,
      svgFile: null,
      mapCoordinates: null,
      pointLatitude: null,
      pointLongitude: null,
      effectiveStart: null,
      effectiveEnd: null,
      version: null,
    },
    areaParticipant: {
      id: null,
      siteId: null,
      areaId: null,
      effectiveStart: null,
      effectiveEnd: null,
    },
    areaType: {
      id: null,
      name: null,
      description: null,
    },
    baselineMethod: {
      id: null,
      name: null,
    },
    contact: {
      id: null,
      machContactId: null,
      organizationId: null,
      name: null,
      title: null,
      phone: null,
      phoneExt: null,
      mobile: null,
      email: null,
      isChiefEngineer: 0,
      isPropertyManager: 0,
      allowNotifyEmail: 0,
      allowNotifyText: 0,
      allowNotifyVoice: 0,
      effectiveStart: null,
      effectiveEnd: null,
      contactSites: [],
    },
    contactOrigin: {
      contactId: null,
      organizationId: null,
      name: null,
      title: null,
      phone: null,
      mobile: null,
      email: null,
      employerId: null,
      supportingOrganization: null,
      contactStatus: null,
      dateCreated: null,
      dateLastModified: null,
      modifiedBy: null,
      roleId: null,
      verifiedCell: null,
      disabledInstantMessages: null,
    },
    contactSite: {
      id: null,
      contactId: null,
      siteId: null,
      isChiefEngineer: null,
      isPropertyManager: null,
      allowNotifyEmail: null,
      allowNotifyText: null,
      allowNotifyVoice: null,
      effectiveStart: null,
      effectiveEnd: null,
    },
    curtailPlan: {
      id: null,
      siteId: null,
      programId: null,
      planText: null,
      dateCreated: null,
      ackRequired: null,
      effectiveStart: null,
      effectiveEnd: null,
    },
    enrollmentRecord: {
      id: null,
      siteId: null,
      accountId: null,
      enrollmentKw: null,
      staticBaseline: null,
      shutdownKw: null,
      programId: null,
      programPriceId: null,
      programPriceOverride: null,
      rampOverrideMinutes: null,
      effectiveStart: null,
      effectiveEnd: null,
      modifiedBy: null,
      modifiedTimestamp: null,
      revenueShares: [],
    },
    event: {
      id: null,
      programId: null,
      isConfirmed: null,
      isProgramTest: null,
      isInternalTest: null,
      curtailDateStart: null,
      curtailDateEnd: null,
      isSubcool: null,
      isWeatherFactorOverride: null,
      overrideThresholdKw: null,
      overrideMax: null,
      overrideMin: null,
      buildings: [],
      eventCommunications: [],
    },
    eventEmailNotification: {
      id: null,
      contactId: null,
      eventId: null,
      emailTemplateId: null,
      emailSubject: null,
      emailBody: null,
      sentTimestamp: null,
      openedTimestamp: null,
      ackTimestamp: null,
    },
    eventEmailTemplate: {
      id: null,
      typeId: null,
      name: null,
      description: null,
      subjectText: null,
      bodyText: null,
    },
    eventEmailType: {
      id: null,
      name: null,
      description: null,
      leadTime: null,
    },
    eventParticipant: {
      id: null,
      eventId: null,
      siteId: null,
    },
    eventPerformance: {
      id: null,
      eventId: null,
      siteId: null,
      accountId: null,
      baseline: null,
      consumption: null,
      performanceData: null,
      performanceAvg: null,
      target: null,
      performanceFactor: null,
      version: null,
      calculationTimestamp: null,
    },
    eventPerformanceHistory: {
      id: null,
      eventId: null,
      siteId: null,
      accountId: null,
      version: null,
      calculationTimestamp: null,
      baseline: null,
      consumption: null,
      performanceData: null,
      performanceAvg: null,
      target: null,
      performanceFactor: null,
    },
    mmaReportCriteria: {
      startDate: null,
      endDate: null,
      allowWeekends: 1, //null,
      programAdminId: null,
      organizationId: null,
      energyTypeId: 1, //null,
      accountId: null,
      resourceId: null,
      accountList: null,
      resourceList: null,
    },
    aclReportCriteria: {
      dateTimeList: null,
      accountList: null,
      resourceList: null,
    },
    program: {
      id: null,
      programAdminId: null,
      baselineMethodId: null,
      name: null,
      isSummer: null,
      isWinter: null,
      marketState: null,
      marketIso: null,
      marketUtility: null,
      isSubcool: null,
      subcoolStartHourOffset: null,
      subcoolEndHourOffset: null,
      isWeatherAdjusted: null,
      weatherAdjustMax: null,
      weatherAdjustMin: null,
    },
    programAdmin: {
      id: null,
      name: null,
      isOperator: null,
      isUtility: null,
    },
    programPrice: {
      id: null,
      programId: null,
      pricePerKw: null,
      priceType: null,
      effectiveStart: null,
      effectiveEnd: null,
    },
    resource: {
      id: null,
      siteId: null,
      accountId: null,
      resourceId: null,
      programAdminId: null,
      effectiveStart: null,
      effectiveEnd: null,
    },
    revenueShare: {
      id: null,
      enrollmentId: null,
      revenueShare: null,
      payee: null,
      effectiveStart: null,
      effectiveEnd: null,
    },
    eventCommunication: {
      id: null,
      eventId: null,
      typeId: null,
      statusId: null,
      name: null,
      description: null,
      // These values are used by the Email Types
      emailTemplateId: null,
      emailSubject: null,
      emailBody: null,
      // These values are used by the SMS Text Types
      smsTemplateId: null,
      smsText: null,
      // These values are used by the Voice Call Types
      voiceFileId: null,
      voiceFilePath: null,
      // These are for scheduling purposes
      schedulePrep: null,
      scheduleSend: null,
    },
  };
  return layout?.[formName] ?? {};
};

export const saveFormToServer = (name) => {
  // convert the formName to saveFormName to use as the methodName
  const methodName = `save${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  return saveToServer(methodName, async (payload, { getState }) => {
    const formData = prepForDatabase({
      ...cleanRecord(name),
      ...getState().curtailment.forms[name],
    });
    return { binds: formData };
  });
};

export const saveArea = saveFormToServer("area");
export const saveAreaType = saveFormToServer("areaType");
export const saveBaselineMethod = saveFormToServer("baselineMethod");
export const saveCurtailPlan = saveFormToServer("curtailPlan");
export const saveEnrollmentRecord = saveFormToServer("enrollmentRecord");
export const saveEventEmailType = saveFormToServer("eventEmailType");
export const saveEventEmailTemplate = saveFormToServer("eventEmailTemplate");
export const saveProgram = saveFormToServer("program");
export const saveProgramPrice = saveFormToServer("programPrice");
export const saveProgramAdmin = saveFormToServer("programAdmin");
export const saveResource = saveFormToServer("resource");

export const saveEvent = saveToServer(
  "saveEvent",
  async (payload, { getState }) => {
    const formData = prepForDatabase({
      ...cleanRecord("event"),
      ...getState().curtailment.forms.event,
    });

    // Load Event Buildings From enrolledSites
    formData.buildings = getState()
      .curtailment.tableData.enrolledSites.filter((item) => !!item.selected)
      .map((item) => ({ siteId: item.siteId }));
    formData.eventCommunications.map((obj) => {
      let { schedulePrep, scheduleSend } = obj;
      if (schedulePrep) {
        schedulePrep = schedulePrep.replace(reDateTime, "$1T$2");
      }
      if (scheduleSend) {
        scheduleSend = scheduleSend.replace(reDateTime, "$1T$2");
      }
      return { ...obj, schedulePrep, scheduleSend };
    });
    return { binds: formData };
  },
);

export const saveEnrollmentRecordEx = saveToServer(
  "saveEnrollmentRecordEx",
  async (payload, { getState }) => {
    const formData = prepForDatabase({
      ...cleanRecord("enrollmentRecord"),
      ...getState().curtailment.forms.enrollmentRecord,
    });
    return { binds: formData };
  },
);

export const saveContactEx = saveToServer(
  "saveContactEx",
  async (payload, { getState }) => {
    const record = { ...getState().curtailment.forms.contact };
    record.phone = record.phone?.replace(/[^\d]/g, "") ?? record.phone;
    record.mobile = record.mobile?.replace(/[^\d]/g, "") ?? record.mobile;
    const formData = prepForDatabase({
      ...cleanRecord("contact"),
      ...record,
    });

    // Does anything need to happen to the contactSites before sending? If so, loop it here

    return { binds: formData };
  },
);

export const deleteFromServer = (name) => {
  // convert the formName to deleteFormName to use as the methodName
  const methodName = `delete${name.charAt(0).toUpperCase()}${name.slice(
    1,
  )}ById`;
  // This could be whittled down to only providing the id field, but for now, send everything
  return getFromServerTopic2(
    "curtailment",
    methodName,
    async (payload, { getState }) => {
      const formData = prepForDatabase({
        ...getState().curtailment.forms[name],
      });
      return { binds: formData };
    },
  );
};

export const deleteArea = deleteFromServer("area");
export const deleteAreaType = deleteFromServer("areaType");
export const deleteBaselineMethod = deleteFromServer("baselineMethod");
export const deleteContact = deleteFromServer("contact");
export const deleteCurtailPlan = deleteFromServer("curtailPlan");
export const deleteEnrollmentRecord = deleteFromServer("enrollmentRecord");
export const deleteEvent = deleteFromServer("event");
export const deleteEventEmailType = deleteFromServer("eventEmailType");
export const deleteEventEmailTemplate = deleteFromServer("eventEmailTemplate");
export const deleteProgram = deleteFromServer("program");
export const deleteProgramPrice = deleteFromServer("programPrice");
export const deleteProgramAdmin = deleteFromServer("programAdmin");
export const deleteResource = deleteFromServer("resource");

export const readIntoFormById = (name) => {
  // convert the formName to readFormNameById to use as the methodName
  const methodName = `read${name.charAt(0).toUpperCase()}${name.slice(1)}ById`;
  return getFromServer(methodName, async (payload) => {
    return { binds: { id: payload } };
  });
};

export const readAreaById = readIntoFormById("area");
export const readAreaTypeById = readIntoFormById("areaType");
export const readBaselineMethodById = readIntoFormById("baselineMethod");
export const readCurtailPlanById = readIntoFormById("curtailPlan");
export const readEnrollmentRecordById = readIntoFormById("enrollmentRecord");
export const readEventById = readIntoFormById("event");
export const readEventEmailTypeById = readIntoFormById("eventEmailType");
export const readEventEmailTemplateById =
  readIntoFormById("eventEmailTemplate");
export const readProgramById = readIntoFormById("program");
export const readProgramPriceById = readIntoFormById("programPrice");
export const readProgramAdminById = readIntoFormById("programAdmin");
export const readResourceById = readIntoFormById("resource");

export const readEnrollmentRecordByIdEx = getFromServer2(
  "readEnrollmentRecordByIdEx",
  async (payload) => {
    return { binds: { id: payload } };
  },
);
export const readEventByIdEx = getFromServer2(
  "readEventByIdEx",
  async (payload) => {
    return { binds: { id: payload } };
  },
);

export const readContactByIdEx = getFromServer2(
  "readContactByIdEx",
  async (payload) => {
    return { binds: { id: payload } };
  },
);
export const readMachContactById = getFromServer2(
  "readMachContactById",
  async (payload) => {
    return { binds: { id: payload } };
  },
);

export const getContactSitesByOrganizationId = getFromServer2(
  "getContactSitesByOrganizationId",
  async (payload) => {
    return { binds: { organizationId: payload } };
  },
);

export const saveThing = saveFormToServer("thing");
export const deleteThing = deleteFromServer("thing");
export const readThingById = readIntoFormById("thing");

/*
loadAccountsBySite accepts an object parameter {siteId, id}
	siteId	the id of the parent site record
	    id	the optional id of the current account record for cases where the account
			is no longer valid for selection, but already exists on the record
*/
export const loadAccountsBySite = getFromServer2(
  "loadAccountsBySite",
  async (payload) => {
    return {
      binds: { siteId: payload?.siteId ?? null, id: payload?.id ?? null },
    };
  },
);

/*
loadProgramPrices accepts an object parameter {siteId, id}
	programId	the id of the parent program record
	       id	the optional id of the current Program Price for cases where the price
				is no longer valid for selection, but already exists on the record
*/
export const loadProgramPricesByProgram = getFromServer2(
  "loadProgramPricesByProgram",
  async (payload) => {
    return {
      binds: {
        programId: payload?.programId ?? null,
        id: payload?.id ?? null,
        effectiveStart: payload?.effectiveStart ?? null,
        effectiveEnd: payload?.effectiveEnd ?? null,
      },
    };
  },
);

/*
loadEventCommunicationPreview accepts an object parameter {eventId, siteId, templateId}
	eventId	the id of the dr_event record
	templateId the id from the dr_event_email_template table to use as the communication
	siteId	the id of the currently selected site from the enrolledSites haystack
*/
export const loadEventCommunicationPreview = getFromServer2(
  "loadEventCommunicationPreview",
  async (payload) => {
    return {
      binds: {
        eventId: payload?.eventId ?? null,
        templateId: payload?.templateId ?? null,
        siteId: payload?.siteId ?? null,
      },
    };
  },
);

/*
loadEventEmailCommunicationPreview accepts an object parameter {eventId, siteId, templateId}
	eventId	the id of the dr_event record
	templateId the id from the dr_event_email_template table to use as the communication
	siteId	the id of the currently selected site from the enrolledSites haystack
*/
export const loadEventEmailCommunicationPreview = getFromServer2(
  "loadEventEmailCommunicationPreview",
  async (payload) => {
    return {
      binds: {
        eventId: payload?.eventId ?? null,
        templateId: payload?.templateId ?? null,
        siteId: payload?.siteId ?? null,
      },
    };
  },
);

/*
loadEventTextCommunicationPreview accepts an object parameter {eventId, siteId, templateId}
	eventId	the id of the dr_event record
	templateId the id from the dr_event_email_template table to use as the communication
	siteId	the id of the currently selected site from the enrolledSites haystack
*/
export const loadEventTextCommunicationPreview = getFromServer2(
  "loadEventTextCommunicationPreview",
  async (payload) => {
    return {
      binds: {
        eventId: payload?.eventId ?? null,
        templateId: payload?.templateId ?? null,
        siteId: payload?.siteId ?? null,
      },
    };
  },
);

/*
loadCallWindowsByProgram accepts an object parameter {programId, curtailDateStart}
	programId	the id of the parent program record
	curtailDateStart	the optional curtailment event start date/time value which defaults to the current date/time
*/
export const loadCallWindowsByProgram = getFromServer2(
  "loadCallWindowsByProgram",
  async (payload) => {
    return {
      binds: {
        programId: payload?.programId ?? null,
        curtailDateStart: payload?.curtailDateStart ?? null,
      },
    };
  },
);

/*
loadAreasByProgram accepts an object parameter {programId, curtailDateStart}
	programId	the id of the parent program record
	curtailDateStart	the optional curtailment event start date/time value which defaults to the current date/time
*/
export const loadAreasByProgram = getFromServer2(
  "loadAreasByProgram",
  async (payload) => {
    return {
      binds: {
        programId: payload?.programId ?? null,
        curtailDateStart: payload?.curtailDateStart ?? null,
      },
    };
  },
);

/*
loadEnrolledSitesByProgram accepts an object parameter {programId, curtailDateStart}
	programId	the id of the parent program record
	curtailDateStart	the optional curtailment event start date/time value which defaults to the current date/time
	eventId	the optional event id value to pull in sites which are already on the event, regardless of program or enrollment status
*/
export const loadEnrolledSitesByProgram = getFromServer2(
  "loadEnrolledSitesByProgram",
  async (payload) => {
    return {
      binds: {
        programId: payload?.programId ?? null,
        curtailDateStart: payload?.curtailDateStart ?? null,
        eventId: payload?.eventId ?? null,
      },
    };
  },
);

export const loadMmaReportResults = getFromServer2(
  "loadMmaReportResults",
  async (payload, { getState }) => {
    const record = { ...getState().curtailment.forms.mmaReportCriteria };
    const formData = prepForDatabase({
      ...cleanRecord("mmaReportCriteria"),
      ...record,
    });
    //console.log({ record, formData });

    return { binds: formData };
  },
);

export const loadAclReportResults = getFromServer2(
  "loadAclReportResults",
  async (payload, { getState }) => {
    const record = { ...getState().curtailment.forms.aclReportCriteria };
    const formData = prepForDatabase({
      ...cleanRecord("aclReportCriteria"),
      ...record,
    });
    //console.log({ record, formData });

    return { binds: formData };
  },
);

/*
	export const saveContactEx = saveToServer(
	  "saveContactEx",
	  async (payload, { getState }) => {
		const record = { ...getState().curtailment.forms.contact };
		record.phone = record.phone?.replace(/[^\d]/g, "") ?? record.phone;
		record.mobile = record.mobile?.replace(/[^\d]/g, "") ?? record.mobile;
		const formData = prepForDatabase({
		  ...cleanRecord("contact"),
		  ...record,
		});
		return { binds: formData };
	  },
	);
  */

export const sanityCheckActiveEnrolledBuildingsMissingTimezones =
  getFromServer2(
    "sanityCheckActiveEnrolledBuildingsMissingTimezones",
    async (payload) => payload,
  );

export const sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram =
  getFromServer2(
    "sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram",
    async (payload) => payload,
  );

export const sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment =
  getFromServer2(
    "sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment",
    async (payload) => payload,
  );

export const getPreviewInfoByEventIdAndSiteId = getFromServer2(
  "getPreviewInfoByEventIdAndSiteId",
  async (payload) => {
    return {
      binds: payload?.binds ?? {
        eventId: payload?.eventId ?? null,
        siteId: payload?.siteId ?? null,
      },
    };
  },
);

// assume the payload is simply a contact id / user id value
export const getEventParticipationByContactId = getFromServer2(
  "getEventParticipationByContactId",
  async (payload) => {
    return {
      binds: {
        contactId: payload ?? null,
      },
    };
  },
);

export const slice = createSlice({
  name: "curtailment",
  initialState: {
    display: "",
    status: IDLE,
    participating: false,
    pendingRequests: 0,
    accounts: [],
    resources: [],
    areas: [],
    zones: [],
    callWindows: [],
    areaTypes: [],
    energyTypes: [],
    organizations: [],
    programAdmins: [],
    programs: [],
    programPrices: [],
    baselineMethods: [],
    sites: [],
    selectedSites: {},
    eventCommunicationPreviewTokens: {},
    eventCommunicationPreview: { emailSubject: "", emailBody: "", smsText: "" },
    voiceFiles: [], // Used by event page communications dropdown and to grab filePath property
    things: [
      { value: "0", label: "Thing 0" },
      { value: "1", label: "Thing 1" },
      { value: "2", label: "Thing 2" },
    ],
    forms: {
      area: prepForForm(cleanRecord("area")),
      areaType: prepForForm(cleanRecord("areaType")),
      baselineMethod: prepForForm(cleanRecord("baselineMethod")),
      contact: prepForForm(cleanRecord("contact")),
      contactOrigin: prepForForm(cleanRecord("contactOrigin")),
      contactSite: prepForForm(cleanRecord("contactSite")),
      curtailPlan: prepForForm(cleanRecord("curtailPlan")),
      enrollmentRecord: prepForForm(cleanRecord("enrollmentRecord")),
      event: prepForForm(cleanRecord("event")),
      eventEmailNotification: prepForForm(
        cleanRecord("eventEmailNotification"),
      ),
      eventEmailTemplate: prepForForm(cleanRecord("eventEmailTemplate")),
      eventEmailType: prepForForm(cleanRecord("eventEmailType")),
      eventPerformance: prepForForm(cleanRecord("eventPerformance")),
      mmaReportCriteria: prepForForm(cleanRecord("mmaReportCriteria")),
      aclReportCriteria: prepForForm(cleanRecord("aclReportCriteria")),
      program: prepForForm(cleanRecord("program")),
      programAdmin: prepForForm(cleanRecord("programAdmin")),
      programPrice: prepForForm(cleanRecord("programPrice")),
      resource: prepForForm(cleanRecord("resource")),
      revenueShare: prepForForm(cleanRecord("revenueShare")),
      eventCommunication: prepForForm(cleanRecord("eventCommunication")),
      thing: {
        id: 1,
        programId: 1,
        textArea: "Things and Stuff",
        richText: "<b>Bold words</b> for a man with giant bird wings...",
        startTime: "00:00",
        effectiveStart: "2023-01-01T00:00:00",
        effectiveEnd: undefined,
      },
    },
    formIssues: {
      area: [],
      areaType: [],
      baselineMethod: [],
      contact: [],
      contactOrigin: [],
      contactSite: [],
      curtailPlan: [],
      enrollmentRecord: [],
      event: [],
      eventEmailNotification: [],
      eventEmailTemplate: [],
      eventEmailType: [],
      eventPerformance: [],
      mmaReportCriteria: [],
      aclReportCriteria: [],
      program: [],
      programAdmin: [],
      programPrice: [],
      resource: [],
      revenueShare: [],
      eventCommunication: [],
      thing: [],
    },
    tableData: {
      curtailPlans: [],
      areas: [],
      areaTypes: [],
      baselineMethods: [],
      contacts: [],
      contactSites: [],
      enrollmentRecords: [],
      enrolledSites: [],
      events: [],
      mmaReportResults: [],
      aclReportResults: [],
      programs: [],
      programAdmins: [],
      things: [],
    },
    tableOpts: {
      areas: {},
      areaTypes: {},
      baselineMethods: {},
      communications: {},
      contacts: {},
      contactSites: {},
      curtailPlans: {},
      emailTemplates: {},
      enrolledSites: {},
      enrollmentRecords: {},
      events: {},
      mmaReportResults: {},
      notifications: {},
      programs: {},
      programAdmins: {},
      resources: {},
      things: {},
    },
    pages: {},
    validators: {
      area: {},
      areaType: {},
      baselineMethod: {},
      contact: {},
      contactSite: {},
      curtailPlan: {},
      enrollmentRecord: {},
      event: {},
      eventEmailNotification: {},
      eventEmailTemplate: {},
      eventEmailType: {},
      eventPerformance: {},
      mmaReportCriteria: {},
      aclReportCriteria: {},
      program: {},
      programAdmin: {},
      programPrice: {},
      resource: {},
      revenueShare: {},
      eventCommunication: {},
      thing: {},
    },
    database: {
      programs: [],
    },
  },
  reducers: {
    changeDisplay: (state, action) => {
      state.display = action.payload;
    },
    changeFormField: (state, action) => {
      const { formName, fieldName, value } = action.payload;
      state.forms[formName] = {
        ...state.forms[formName],
        [fieldName]: value,
      };
    },
    clearForm: (state, action) => {
      const formName = action.payload;
      state.forms[formName] = prepForForm(cleanRecord(formName));
    },
    clearTableData: (state, action) => {
      const tableName = action.payload;
      state.tableData[tableName] = [];
    },
    addRevenueShare: (state, action) => {
      state.forms.enrollmentRecord.revenueShares = [
        ...(state.forms.enrollmentRecord?.revenueShares ?? []),
        action.payload,
      ];
    },
    swapRevenueShare: (state, action) => {
      const { obj, index } = action.payload;
      state.forms.enrollmentRecord.revenueShares = [
        ...(state.forms.enrollmentRecord?.revenueShares?.slice(0, index) ?? []),
        obj,
        ...(state.forms.enrollmentRecord?.revenueShares?.slice(index + 1) ??
          []),
      ];
    },

    addEventCommunication: (state, action) => {
      state.forms.event.eventCommunications = [
        ...(state.forms.event?.eventCommunications ?? []),
        action.payload,
      ];
    },
    swapEventCommunication: (state, action) => {
      const { obj, index } = action.payload;
      state.forms.event.eventCommunications = [
        ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
        obj,
        ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
      ];
    },
    setContactSiteEnabledByIndex: (state, action) => {
      const { index, enabled } = action.payload;
      // index can be an array, I decided
      (index instanceof Array ? index : [index]).map((i) => {
        state.forms.contact.contactSites[i] = {
          ...state.forms.contact.contactSites[i],
          enabled,
        };
      });
    },
    setContactSitePropertyByIndex: (state, action) => {
      const { index, ...obj } = action.payload;
      // index can be an array, I decided
      (index instanceof Array ? index : [index]).map((i) => {
        state.forms.contact.contactSites[i] = {
          ...state.forms.contact.contactSites[i],
          ...obj,
        };
      });
    },
    swapContactSite: (state, action) => {
      const { obj, index } = action.payload;
      state.forms.contact.contactSites = [
        ...(state.forms.contact?.contactSites?.slice(0, index) ?? []),
        obj,
        ...(state.forms.contact?.contactSites?.slice(index + 1) ?? []),
      ];
    },

    //setBuildingSelectionByIndex({index:<int> | <array>, selected: <bool>})
    setBuildingSelectionByIndex: (state, action) => {
      const { index, selected } = action.payload;
      // index can be an array, I decided
      (index instanceof Array ? index : [index]).map((i) => {
        state.tableData.enrolledSites[i] = {
          ...state.tableData.enrolledSites[i],
          selected,
        };
        if (selected) {
          state.selectedSites[state.tableData.enrolledSites[i].siteId] = true;
        } else {
          delete state.selectedSites[state.tableData.enrolledSites[i].siteId];
        }
      });
    },
    //setBuildingSelectionByCallWindow({callWindow:<string>, selected: <bool>})
    setBuildingSelectionByCallWindow: (state, action) => {
      const { callWindow, selected } = action.payload;
      for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
        if (state.tableData.enrolledSites[i].callWindow === callWindow) {
          state.tableData.enrolledSites[i] = {
            ...state.tableData.enrolledSites[i],
            selected,
          };
          if (selected) {
            state.selectedSites[state.tableData.enrolledSites[i].siteId] = true;
          } else {
            delete state.selectedSites[state.tableData.enrolledSites[i].siteId];
          }
        }
      }
    },
    //setBuildingSelectionByAreaId({areaId:<int>, selected: <bool>})
    setBuildingSelectionByAreaId: (state, action) => {
      const { areaId, selected } = action.payload;
      for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
        // areaId is now an array of values and needs to be treated as such for comparisons.
        if (state.tableData.enrolledSites[i].areaId?.includes(areaId)) {
          state.tableData.enrolledSites[i] = {
            ...state.tableData.enrolledSites[i],
            selected,
          };
          if (selected) {
            state.selectedSites[state.tableData.enrolledSites[i].siteId] = true;
          } else {
            delete state.selectedSites[state.tableData.enrolledSites[i].siteId];
          }
        }
      }
    },
    // setEnrolledSitesSelected(bool);
    setEnrolledSitesSelected: (state, action) => {
      const selected = action.payload;
      for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
        state.tableData.enrolledSites[i] = {
          ...state.tableData.enrolledSites[i],
          selected,
        };
        if (selected) {
          state.selectedSites[state.tableData.enrolledSites[i].siteId] =
            selected;
        } else {
          delete state.selectedSites[state.tableData.enrolledSites[i].siteId];
        }
      }
    },
    stashEnrolledSites: (state) => {
      const selectedSites = {};
      for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
        if (state.tableData.enrolledSites[i].selected) {
          selectedSites[state.tableData.enrolledSites[i].siteId] = true;
        }
      }
      state.selectedSites = { ...selectedSites };
    },
    restoreEnrolledSites: (state) => {
      for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
        if (state.selectedSites[state.tableData.enrolledSites[i].siteId]) {
          state.tableData.enrolledSites[i].selected =
            state.selectedSites[state.tableData.enrolledSites[i].siteId];
        }
      }
    },
    showSelectedSites: (state) => {
      console.log("selectedSites", state.selectedSites);
    },
    clearSelectedSites: (state) => {
      state.selectedSites = {};
    },
    setTableOpts: (state, action) => {
      const { tableName, options } = action.payload;
      state.tableOpts[tableName] = {
        ...(state.tableOpts?.[tableName] ?? { filters: [], sortBy: [] }),
        ...(options ?? { filters: [], sortBy: [] }),
      };
    },
    generateEventEmailCommunicationPreview: (state, action) => {
      const { emailSubject, emailBody } = action.payload;
      let tempEmailSubject = emailSubject;
      let tempEmailBody = emailBody;
      Object.keys(state.eventCommunicationPreviewTokens).forEach((token) => {
        tempEmailSubject = tempEmailSubject.replaceAll(
          `%${token}%`,
          state.eventCommunicationPreviewTokens[token],
        );
        tempEmailBody = tempEmailBody.replaceAll(
          `%${token}%`,
          state.eventCommunicationPreviewTokens[token],
        );
      });
      state.eventCommunicationPreview.emailSubject = tempEmailSubject;
      state.eventCommunicationPreview.emailBody = tempEmailBody;
    },
    generateEventTextCommunicationPreview: (state, action) => {
      const { smsText } = action.payload;
      let tempSmsText = smsText;
      Object.keys(state.eventCommunicationPreviewTokens).forEach((token) => {
        tempSmsText = tempSmsText.replaceAll(
          `%${token}%`,
          state.eventCommunicationPreviewTokens?.[token],
        );
      });
      state.eventCommunicationPreview.smsText = tempSmsText;
    },
    generateEventCommunicationPreview: (state, action) => {
      const { emailSubject, emailBody, smsText } = action.payload;
      let tempEmailSubject = emailSubject ?? "";
      let tempEmailBody = emailBody ?? "";
      let tempSmsText = smsText ?? "";
      Object.keys(state.eventCommunicationPreviewTokens).forEach((token) => {
        tempEmailSubject = tempEmailSubject.replaceAll(
          `%${token}%`,
          state.eventCommunicationPreviewTokens[token] ?? "",
        );
        tempEmailBody = tempEmailBody.replaceAll(
          `%${token}%`,
          state.eventCommunicationPreviewTokens[token] ?? "",
        );
        tempSmsText = tempSmsText.replaceAll(
          `%${token}%`,
          state.eventCommunicationPreviewTokens?.[token] ?? "",
        );
      });
      state.eventCommunicationPreview.emailSubject = tempEmailSubject;
      state.eventCommunicationPreview.emailBody = tempEmailBody;
      state.eventCommunicationPreview.smsText = tempSmsText;
    },

    clearEventCommunicationPreview: (state) => {
      state.eventCommunicationPreviewTokens = {};
      state.eventCommunicationPreview = {
        emailSubject: "",
        emailBody: "",
        smsText: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveArea.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveArea.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          const data = prepForForm(action.payload.payload);
          data.effectiveStart = data.effectiveStart?.replace(
            reDateTime,
            "$1T$2",
          );
          data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
          state.forms.area = data;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(saveArea.rejected, (state, action) => {
        console.error("saveArea.rejected state", state);
        console.error("saveArea.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(getAreaTypesForDropdown.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          state.areaTypes = action.payload.payload;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
      })

      .addCase(saveAreaType.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveAreaType.fulfilled, (state, action) => {
        state.forms.areaType = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveAreaType.rejected, (state, action) => {
        console.error("saveAreaType.rejected state", state);
        console.error("saveAreaType.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveCurtailPlan.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveCurtailPlan.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          const data = prepForForm(action.payload.payload);
          data.effectiveStart = data.effectiveStart?.replace(
            reDateTime,
            "$1T$2",
          );
          data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
          data.dateCreated = data.dateCreated?.replace(reDateTime, "$1T$2");
          state.forms.curtailPlan = data;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(saveCurtailPlan.rejected, (state, action) => {
        console.error("saveCurtailPlan.rejected state", state);
        console.error("saveCurtailPlan.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveEnrollmentRecordEx.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveEnrollmentRecordEx.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          const data = prepForForm(action.payload.payload);
          data.effectiveStart = data.effectiveStart?.replace(
            reDateTime,
            "$1T$2",
          );
          data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
          data.modifiedTimestamp = data.modifiedTimestamp?.replace(
            reDateTime,
            "$1T$2",
          );
          for (let i = 0; i < data.revenueShares.length; i++) {
            data.revenueShares[i].effectiveStart = data.revenueShares[
              i
            ].effectiveStart?.replace(reDateTime, "$1T$2");
            data.revenueShares[i].effectiveEnd = data.revenueShares[
              i
            ].effectiveEnd?.replace(reDateTime, "$1T$2");
          }
          state.forms.enrollmentRecord = data;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("saveEnrollmentRecordEx ERROR action", action);
        }
        completedRequest(state);
      })
      .addCase(saveEnrollmentRecordEx.rejected, (state, action) => {
        console.error("saveEnrollmentRecordEx.rejected state", state);
        console.error("saveEnrollmentRecordEx.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveEvent.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveEvent.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          const data = prepForForm(action.payload.payload);
          data.curtailDateStart = data.curtailDateStart?.replace(
            reDateTime,
            "$1T$2",
          );
          data.curtailDateEnd = data.curtailDateEnd?.replace(
            reDateTime,
            "$1T$2",
          );
          for (let i = 0; i < data?.eventCommunications?.length; i++) {
            data.eventCommunications[i].schedulePrep = data.eventCommunications[
              i
            ].schedulePrep?.replace(reDateTime, "$1T$2");
            data.eventCommunications[i].scheduleSend = data.eventCommunications[
              i
            ].scheduleSend?.replace(reDateTime, "$1T$2");
          }

          state.forms.event = data;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("saveEvent ERROR action", action);
        }
        completedRequest(state);
      })
      .addCase(saveEvent.rejected, (state, action) => {
        console.error("saveEvent.rejected state", state);
        console.error("saveEvent.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(loadEventCommunicationPreview.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.eventCommunicationPreview = action.payload.payload;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("loadEventCommunicationPreview ERROR action", action);
        }
      })
      .addCase(loadEventCommunicationPreview.rejected, (state, action) => {
        console.error("loadEventCommunicationPreview.rejected state", state);
        console.error("loadEventCommunicationPreview.rejected action", action);
      })

      .addCase(
        loadEventEmailCommunicationPreview.fulfilled,
        (state, action) => {
          if (action?.payload?.rc === "OK") {
            state.eventCommunicationPreview = action.payload.payload;
          } else {
            console.error("ERROR", action.payload?.context?.sysMessages);
            console.error(
              "loadEventEmailCommunicationPreview ERROR action",
              action,
            );
          }
        },
      )
      .addCase(loadEventEmailCommunicationPreview.rejected, (state, action) => {
        console.error(
          "loadEventEmailCommunicationPreview.rejected state",
          state,
        );
        console.error(
          "loadEventEmailCommunicationPreview.rejected action",
          action,
        );
      })

      .addCase(loadEventTextCommunicationPreview.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.eventCommunicationPreview = action.payload.payload;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error(
            "loadEventTextCommunicationPreview ERROR action",
            action,
          );
        }
      })
      .addCase(loadEventTextCommunicationPreview.rejected, (state, action) => {
        console.error(
          "loadEventTextCommunicationPreview.rejected state",
          state,
        );
        console.error(
          "loadEventTextCommunicationPreview.rejected action",
          action,
        );
      })

      .addCase(prepareEventEmailCommunications.pending, (state, action) => {
        const id = action?.meta?.arg ?? 0;
        if (id) {
          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === id,
          );
          const eventCommunication =
            state.forms.event.eventCommunications[index];
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            { ...eventCommunication, pending: "Preparing" },
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
        }

        //pendingRequest(state, SAVING);
      })
      .addCase(prepareEventEmailCommunications.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          // Swap the eventCommunications object that was just prepared with the new fresh copy that was returned
          const { eventCommunication, preparedCount } = action.payload.payload;

          eventCommunication.schedulePrep = resolveDate(
            eventCommunication.schedulePrep,
          );
          eventCommunication.scheduleSend = resolveDate(
            eventCommunication.scheduleSend,
          );

          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === eventCommunication.id,
          );
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            eventCommunication,
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
          console.log({ preparedCount });
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("prepareEventEmailCommunications ERROR action", action);
        }

        //completedRequest(state);
      })
      .addCase(prepareEventEmailCommunications.rejected, (state, action) => {
        // Need to address the "pending" property of the
        // eventCommunication record when rejected.
        // Check action, do we have an id or index?
        console.error("prepareEventEmailCommunications.rejected state", state);
        console.error(
          "prepareEventEmailCommunications.rejected action",
          action,
        );
        //rejectedRequest(state);
      })

      .addCase(prepareEventTextCommunications.pending, (state, action) => {
        const id = action?.meta?.arg ?? 0;
        if (id) {
          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === id,
          );
          const eventCommunication =
            state.forms.event.eventCommunications[index];
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            { ...eventCommunication, pending: "Preparing" },
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
        }
        //pendingRequest(state, SAVING);
      })
      .addCase(prepareEventTextCommunications.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          // Swap the eventCommunications object that was just prepared with the new fresh copy that was returned
          const { eventCommunication, preparedCount } = action.payload.payload;

          eventCommunication.schedulePrep = resolveDate(
            eventCommunication.schedulePrep,
          );
          eventCommunication.scheduleSend = resolveDate(
            eventCommunication.scheduleSend,
          );

          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === eventCommunication.id,
          );
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            eventCommunication,
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
          console.log({ preparedCount });
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("prepareEventTextCommunications ERROR action", action);
        }

        //completedRequest(state);
      })
      .addCase(prepareEventTextCommunications.rejected, (state, action) => {
        // Need to address the "pending" property of the
        // eventCommunication record when rejected.
        // Check action, do we have an id or index?
        console.error("prepareEventTextCommunications.rejected state", state);
        console.error("prepareEventTextCommunications.rejected action", action);
        //rejectedRequest(state);
      })

      .addCase(prepareEventVoiceCommunications.pending, (state, action) => {
        const id = action?.meta?.arg ?? 0;
        if (id) {
          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === id,
          );
          const eventCommunication =
            state.forms.event.eventCommunications[index];
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            { ...eventCommunication, pending: "Preparing" },
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
        }
        //pendingRequest(state, SAVING);
      })
      .addCase(prepareEventVoiceCommunications.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          // Swap the eventCommunications object that was just prepared with the new fresh copy that was returned
          const { eventCommunication, preparedCount } = action.payload.payload;

          eventCommunication.schedulePrep = resolveDate(
            eventCommunication.schedulePrep,
          );
          eventCommunication.scheduleSend = resolveDate(
            eventCommunication.scheduleSend,
          );

          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === eventCommunication.id,
          );
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            eventCommunication,
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
          console.log({ preparedCount });
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("prepareEventVoiceCommunications ERROR action", action);
        }

        //completedRequest(state);
      })
      .addCase(prepareEventVoiceCommunications.rejected, (state, action) => {
        // Need to address the "pending" property of the
        // eventCommunication record when rejected.
        // Check action, do we have an id or index?
        console.error("prepareEventVoiceCommunications.rejected state", state);
        console.error(
          "prepareEventVoiceCommunications.rejected action",
          action,
        );
        //rejectedRequest(state);
      })

      .addCase(sendEventCommunications.pending, (state, action) => {
        const id = action?.meta?.arg ?? 0;
        if (id) {
          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === id,
          );
          const eventCommunication =
            state.forms.event.eventCommunications[index];
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            { ...eventCommunication, pending: "Sending" },
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
        }
        //pendingRequest(state, SAVING);
      })
      .addCase(sendEventCommunications.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          // Be aware of an "okay, but..." messaging situation for the "nothing left to do here" scenario
          // Swap the eventCommunications object that was just prepared with the new fresh copy that was returned
          const { eventCommunication, counts } = action.payload.payload;

          const index = state.forms.event.eventCommunications.findIndex(
            (obj) => obj.id === eventCommunication.id,
          );
          state.forms.event.eventCommunications = [
            ...(state.forms.event?.eventCommunications?.slice(0, index) ?? []),
            eventCommunication,
            ...(state.forms.event?.eventCommunications?.slice(index + 1) ?? []),
          ];
          console.log({ counts });
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("sendEventCommunications ERROR action", action);
        }
        //completedRequest(state);
      })
      .addCase(sendEventCommunications.rejected, (state, action) => {
        // Need to address the "pending" property of the
        // eventCommunication record when rejected.
        // Check action, do we have an id or index?
        console.error("sendEventCommunications.rejected state", state);
        console.error("sendEventCommunications.rejected action", action);
        //rejectedRequest(state);
      })

      .addCase(saveBaselineMethod.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveBaselineMethod.fulfilled, (state, action) => {
        state.forms.baselineMethod = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveBaselineMethod.rejected, (state, action) => {
        console.error("saveBaselineMethod.rejected state", state);
        console.error("saveBaselineMethod.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveEventEmailType.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveEventEmailType.fulfilled, (state, action) => {
        state.forms.eventEmailType = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveEventEmailType.rejected, (state, action) => {
        console.error("saveEventEmailType.rejected state", state);
        console.error("saveEventEmailType.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveEventEmailTemplate.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveEventEmailTemplate.fulfilled, (state, action) => {
        state.forms.eventEmailTemplate = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveEventEmailTemplate.rejected, (state, action) => {
        console.error("saveEventEmailTemplate.rejected state", state);
        console.error("saveEventEmailTemplate.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveProgram.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveProgram.fulfilled, (state, action) => {
        state.forms.program = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveProgram.rejected, (state, action) => {
        console.error("saveProgram.rejected state", state);
        console.error("saveProgram.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveProgramAdmin.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveProgramAdmin.fulfilled, (state, action) => {
        state.forms.programAdmin = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveProgramAdmin.rejected, (state, action) => {
        console.error("saveProgramAdmin.rejected state", state);
        console.error("saveProgramAdmin.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveProgramPrice.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveProgramPrice.fulfilled, (state, action) => {
        state.forms.programPrice = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveProgramPrice.rejected, (state, action) => {
        console.error("saveProgramPrice.rejected state", state);
        console.error("saveProgramPrice.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveResource.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveResource.fulfilled, (state, action) => {
        state.forms.resource = action.payload.response; // This is assuming success and perhaps shouldn't be
        completedRequest(state);
      })
      .addCase(saveResource.rejected, (state, action) => {
        console.error("saveResource.rejected state", state);
        console.error("saveResource.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(loadValidators.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(loadValidators.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          const data =
            typeof action.payload.payload === "string"
              ? JSON.parse(action.payload.payload)
              : action.payload.payload;

          for (const formName in data) {
            if (state.validators?.[formName] !== undefined) {
              state.validators[formName] = data[formName];
            }
          }
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(loadValidators.rejected, (state, action) => {
        console.error("loadValidators.rejected state", state);
        console.error("loadValidators.rejected action", action);
        rejectedRequest(state);
      })
      .addCase(readAllPrograms.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.database.programs = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(readAllPrograms.rejected, (state, action) => {
        console.error("readAllPrograms.rejected state", state);
        console.error("readAllPrograms.rejected action", action);
      })

      .addCase(loadAccountsBySite.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.accounts = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(loadAccountsBySite.rejected, (state, action) => {
        console.error("loadAccountsBySite.rejected state", state);
        console.error("loadAccountsBySite.rejected action", action);
      })

      .addCase(loadProgramPricesByProgram.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.programPrices = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(loadProgramPricesByProgram.rejected, (state, action) => {
        console.error("loadProgramPricesByProgram.rejected state", state);
        console.error("loadProgramPricesByProgram.rejected action", action);
      })

      .addCase(loadCallWindowsByProgram.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.callWindows = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(loadCallWindowsByProgram.rejected, (state, action) => {
        console.error("loadCallWindowsByProgram.rejected state", state);
        console.error("loadCallWindowsByProgram.rejected action", action);
      })

      .addCase(loadAreasByProgram.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.areas = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(loadAreasByProgram.rejected, (state, action) => {
        console.error("loadAreasByProgram.rejected state", state);
        console.error("loadAreasByProgram.rejected action", action);
      })

      .addCase(getZonesForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.zones = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(getZonesForDropdown.rejected, (state, action) => {
        console.error("getZonesForDropdown.rejected state", state);
        console.error("getZonesForDropdown.rejected action", action);
      })

      .addCase(loadEnrolledSitesByProgram.pending, (state) => {
        const selectedSites = {};
        for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
          if (state.tableData.enrolledSites[i].selected) {
            selectedSites[state.tableData.enrolledSites[i].siteId] = true;
          }
        }
        state.selectedSites = { ...selectedSites };
      })
      .addCase(loadEnrolledSitesByProgram.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.enrolledSites = action.payload.payload.map((row) => {
            row.marketState = row.marketState
              ? JSON.parse(row.marketState)
              : [];
            row.areaId = row.areaId ? JSON.parse(row.areaId) : [];
            return row;
          });
          for (let i = 0; i < state.tableData.enrolledSites.length; i++) {
            if (state.selectedSites[state.tableData.enrolledSites[i].siteId]) {
              state.tableData.enrolledSites[i].selected =
                state.selectedSites[state.tableData.enrolledSites[i].siteId];
            }
          }
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(loadEnrolledSitesByProgram.rejected, (state, action) => {
        console.error("loadEnrolledSitesByProgram.rejected state", state);
        console.error("loadEnrolledSitesByProgram.rejected action", action);
      })

      .addCase(loadMmaReportResults.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(loadMmaReportResults.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.mmaReportResults = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(loadMmaReportResults.rejected, (state, action) => {
        console.error("loadMmaReportResults.rejected state", state);
        console.error("loadMmaReportResults.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(loadAclReportResults.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(loadAclReportResults.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.aclReportResults = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(loadAclReportResults.rejected, (state, action) => {
        console.error("loadAclReportResults.rejected state", state);
        console.error("loadAclReportResults.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(getProgramAdminsForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.programAdmins = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(getBaselineMethodsForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.baselineMethods = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })

      .addCase(getProgramsForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.programs = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(getSitesForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.sites = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })

      .addCase(getOrganizationsForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.organizations = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })
      .addCase(getEnergyTypesForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.energyTypes = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })

      .addCase(getVoiceFilesForDropdown.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.voiceFiles = action.payload.payload;
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
      })

      .addCase(readAllCurtailPlansForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllCurtailPlansForTable.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.curtailPlans = action.payload.payload.map((row) => {
            row.dateCreated = row.dateCreated?.replace(reDateTime, "$1T$2");
            row.effectiveStart = row.effectiveStart?.replace(
              reDateTime,
              "$1T$2",
            );
            row.effectiveEnd = row.effectiveEnd?.replace(reDateTime, "$1T$2");
            return row;
          });
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readAllCurtailPlansForTable.rejected, (state, action) => {
        console.error("readAllCurtailPlansForTable.rejected state", state);
        console.error("readAllCurtailPlansForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllAreasForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllAreasForTable.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.areas = action.payload.payload.map((row) => {
            row.effective_start = row.effective_start?.replace(
              reDateTime,
              "$1T$2",
            );
            row.effective_end = row.effective_end?.replace(reDateTime, "$1T$2");
            return row;
          });
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readAllAreasForTable.rejected, (state, action) => {
        console.error("readAllAreasForTable.rejected state", state);
        console.error("readAllAreasForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllAreaTypesForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllAreaTypesForTable.fulfilled, (state, action) => {
        // So something with state here using the data returned by action
        state.tableData.areaTypes = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllAreaTypesForTable.rejected, (state, action) => {
        console.error("readAllAreaTypesForTable.rejected state", state);
        console.error("readAllAreaTypesForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllBaselineMethodsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllBaselineMethodsForTable.fulfilled, (state, action) => {
        state.tableData.baselineMethods = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllBaselineMethodsForTable.rejected, (state, action) => {
        console.error("readAllBaselineMethodsForTable.rejected state", state);
        console.error("readAllBaselineMethodsForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllContactsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllContactsForTable.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.contacts = action.payload.payload.map((row) => {
            row.effectiveStart = row.effectiveStart?.replace(
              reDateTime,
              "$1T$2",
            );
            row.effectiveEnd = row.effectiveEnd?.replace(reDateTime, "$1T$2");
            return row;
          });
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readAllContactsForTable.rejected, (state, action) => {
        console.error("readAllContactsForTable.rejected state", state);
        console.error("readAllContactsForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllEnrollmentRecordsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllEnrollmentRecordsForTable.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.enrollmentRecords = action.payload.payload.map(
            (row) => {
              row.effectiveStart = row.effectiveStart?.replace(
                reDateTime,
                "$1T$2",
              );
              row.effectiveEnd = row.effectiveEnd?.replace(reDateTime, "$1T$2");
              return row;
            },
          );
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readAllEnrollmentRecordsForTable.rejected, (state, action) => {
        console.error("readAllEnrollmentRecordsForTable.rejected state", state);
        console.error(
          "readAllEnrollmentRecordsForTable.rejected action",
          action,
        );
        rejectedRequest(state);
      })

      .addCase(readAllEventsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllEventsForTable.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.tableData.events = action.payload.payload.map((row) => {
            row.curtailDateStart = row.curtailDateStart?.replace(
              reDateTime,
              "$1T$2",
            );
            row.curtailDateEnd = row.curtailDateEnd?.replace(
              reDateTime,
              "$1T$2",
            );
            return row;
          });
        } else {
          console.error("ERROR", action?.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readAllEventsForTable.rejected, (state, action) => {
        console.error("readAllEventsForTable.rejected state", state);
        console.error("readAllEventsForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllEventEmailTemplatesForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(
        readAllEventEmailTemplatesForTable.fulfilled,
        (state, action) => {
          state.tableData.eventEmailTemplates = action.payload.response;
          completedRequest(state);
        },
      )
      .addCase(readAllEventEmailTemplatesForTable.rejected, (state, action) => {
        console.error(
          "readAllEventEmailTemplatesForTable.rejected state",
          state,
        );
        console.error(
          "readAllEventEmailTemplatesForTable.rejected action",
          action,
        );
        rejectedRequest(state);
      })

      .addCase(readAllEventEmailTypesForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllEventEmailTypesForTable.fulfilled, (state, action) => {
        state.tableData.eventEmailTypes = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllEventEmailTypesForTable.rejected, (state, action) => {
        console.error("readAllEventEmailTypesForTable.rejected state", state);
        console.error("readAllEventEmailTypesForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllEventEmailNotificationsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(
        readAllEventEmailNotificationsForTable.fulfilled,
        (state, action) => {
          state.tableData.eventEmailNotifications = action.payload.response;
          completedRequest(state);
        },
      )
      .addCase(
        readAllEventEmailNotificationsForTable.rejected,
        (state, action) => {
          console.error(
            "readAllEventEmailNotificationsForTable.rejected state",
            state,
          );
          console.error(
            "readAllEventEmailNotificationsForTable.rejected action",
            action,
          );
          rejectedRequest(state);
        },
      )

      .addCase(readAllProgramAdminsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllProgramAdminsForTable.fulfilled, (state, action) => {
        state.tableData.programAdmins = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllProgramAdminsForTable.rejected, (state, action) => {
        console.error("readAllProgramAdminsForTable.rejected state", state);
        console.error("readAllProgramAdminsForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllProgramsForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllProgramsForTable.fulfilled, (state, action) => {
        state.tableData.programs = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllProgramsForTable.rejected, (state, action) => {
        console.error("readAllProgramsForTable.rejected state", state);
        console.error("readAllProgramsForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllProgramPricesForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllProgramPricesForTable.fulfilled, (state, action) => {
        state.tableData.programPrices = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllProgramPricesForTable.rejected, (state, action) => {
        console.error("readAllProgramPricesForTable.rejected state", state);
        console.error("readAllProgramPricesForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllResourcesForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllResourcesForTable.fulfilled, (state, action) => {
        state.tableData.resources = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllResourcesForTable.rejected, (state, action) => {
        console.error("readAllResourcesForTable.rejected state", state);
        console.error("readAllResourcesForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAllRevenueSharesForTable.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAllRevenueSharesForTable.fulfilled, (state, action) => {
        state.tableData.revenueShares = action.payload.response;
        completedRequest(state);
      })
      .addCase(readAllRevenueSharesForTable.rejected, (state, action) => {
        console.error("readAllRevenueSharesForTable.rejected state", state);
        console.error("readAllResourcesForTable.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAreaById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAreaById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        data.effectiveStart = data.effectiveStart?.replace(reDateTime, "$1T$2");
        data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
        state.forms.area = data;
        completedRequest(state);
      })
      .addCase(readAreaById.rejected, (state, action) => {
        console.error("readAreaById.rejected state", state);
        console.error("readAreaById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readAreaTypeById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readAreaTypeById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        data.effectiveStart = data.effectiveStart?.replace(reDateTime, "$1T$2");
        data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
        state.forms.areaType = data;
        completedRequest(state);
      })
      .addCase(readAreaTypeById.rejected, (state, action) => {
        console.error("readAreaTypeById.rejected state", state);
        console.error("readAreaTypeById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readBaselineMethodById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readBaselineMethodById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        state.forms.baselineMethod = data;
        completedRequest(state);
      })
      .addCase(readBaselineMethodById.rejected, (state, action) => {
        console.error("readBaselineMethodById.rejected state", state);
        console.error("readBaselineMethodById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readCurtailPlanById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readCurtailPlanById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        data.effectiveStart = data.effectiveStart?.replace(reDateTime, "$1T$2");
        data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
        data.dateCreated = data.dateCreated?.replace(reDateTime, "$1T$2");
        state.forms.curtailPlan = data;
        completedRequest(state);
      })
      .addCase(readCurtailPlanById.rejected, (state, action) => {
        console.error("readCurtailPlanById.rejected state", state);
        console.error("readCurtailPlanById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readEnrollmentRecordById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readEnrollmentRecordById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        state.forms.enrollmentRecord = data;
        completedRequest(state);
      })
      .addCase(readEnrollmentRecordById.rejected, (state, action) => {
        console.error("readEnrollmentRecordById.rejected state", state);
        console.error("readEnrollmentRecordById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readEnrollmentRecordByIdEx.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readEnrollmentRecordByIdEx.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          const data = prepForForm(action.payload.payload);
          data.effectiveStart = data.effectiveStart?.replace(
            reDateTime,
            "$1T$2",
          );
          data.effectiveEnd = data.effectiveEnd?.replace(reDateTime, "$1T$2");
          data.modifiedTimestamp = data.modifiedTimestamp?.replace(
            reDateTime,
            "$1T$2",
          );
          for (let i = 0; i < data?.revenueShares?.length; i++) {
            data.revenueShares[i].effectiveStart = data.revenueShares[
              i
            ].effectiveStart?.replace(reDateTime, "$1 $2");
            data.revenueShares[i].effectiveEnd = data.revenueShares[
              i
            ].effectiveEnd?.replace(reDateTime, "$1 $2");
          }
          state.forms.enrollmentRecord = data;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readEnrollmentRecordByIdEx.rejected, (state, action) => {
        console.error("readEnrollmentRecordByIdEx.rejected state", state);
        console.error("readEnrollmentRecordByIdEx.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(getContactSitesByOrganizationId.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          // This should return an array of contact sites
          // Consider storing the existing ones before overwriting the new ones (store, overwrite, replace)
          const contactSites = action.payload.payload;
          for (let i = 0; i < contactSites?.length; i++) {
            contactSites[i].effectiveStart =
              contactSites[i].effectiveStart?.replace(reDateTime, "$1T$2") ??
              contactSites[i].effectiveStart;
            contactSites[i].effectiveEnd =
              contactSites[i].effectiveEnd?.replace(reDateTime, "$1T$2") ??
              contactSites[i].effectiveEnd;
          }
          state.forms.contact.contactSites = contactSites;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
      })
      .addCase(getContactSitesByOrganizationId.rejected, (state, action) => {
        console.error("getContactSitesByOrganizationId.rejected state", state);
        console.error(
          "getContactSitesByOrganizationId.rejected action",
          action,
        );
      })
      .addCase(readMachContactById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readMachContactById.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          state.forms.contact = prepForForm(cleanRecord("contact"));
          const data = prepForForm(action.payload.payload);
          // ensure the record gets the originating mach contactId as it doesn't already have one.
          state.forms.contact.machContactId = data.contactId;

          data.phone = formatPhoneNumber(data.phone);
          data.mobile = formatPhoneNumber(data.mobile);

          // Fix our dates because they aren't actually UTC based, so the 'z' at the end is a lie.
          data.dateCreated =
            data.dateCreated?.replace(reDateTime, "$1T$2") ?? data.dateCreated;
          data.dateLastModified =
            data.dateLastModified?.replace(reDateTime, "$1T$2") ??
            data.dateLastModified;
          state.forms.contact.effectiveStart = data.dateCreated; // Preset the start to the mach contacts creation date
          state.forms.contactOrigin = data; //prepForForm(action.payload.payload);
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readMachContactById.rejected, (state, action) => {
        console.error("readMachContactById.rejected state", state);
        console.error("readMachContactById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readContactByIdEx.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readContactByIdEx.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          // preserve the contactSites as nulls do not survive the call to prepForForm
          const {
            contactSites = [],
            origin,
            ...contact
          } = action.payload.payload;

          contact.phone = formatPhoneNumber(contact.phone);
          contact.mobile = formatPhoneNumber(contact.mobile);

          contact.effectiveStart =
            contact.effectiveStart?.replace(reDateTime, "$1T$2") ??
            contact.effectiveStart;
          contact.effectiveEnd =
            contact.effectiveEnd?.replace(reDateTime, "$1T$2") ??
            contact.effectiveEnd;
          // Fix our dates because they aren't actually UTC based, so the 'z' at the end is a lie.
          if (origin) {
            origin.phone = formatPhoneNumber(origin.phone);
            origin.mobile = formatPhoneNumber(origin.mobile);

            origin.dateCreated =
              origin.dateCreated?.replace(reDateTime, "$1T$2") ??
              origin.dateCreated;
            origin.dateLastModified =
              origin.dateLastModified?.replace(reDateTime, "$1T$2") ??
              origin.dateLastModified;
          }
          for (let i = 0; i < contactSites?.length; i++) {
            contactSites[i].effectiveStart =
              contactSites[i].effectiveStart?.replace(reDateTime, "$1T$2") ??
              contactSites[i].effectiveStart;
            contactSites[i].effectiveEnd =
              contactSites[i].effectiveEnd?.replace(reDateTime, "$1T$2") ??
              contactSites[i].effectiveEnd;
          }

          state.forms.contact = {
            ...prepForForm(contact),
            contactSites: prepForDatabase(contactSites),
          }; // remember, we don't want to lose our nulls in contactSites
          state.forms.contactOrigin = prepForForm(
            origin ?? cleanRecord("contactOrigin"),
          );
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readContactByIdEx.rejected, (state, action) => {
        console.error("readContactByIdEx.rejected state", state);
        console.error("readContactByIdEx.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(saveContactEx.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(saveContactEx.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          // preserve the contactSites as nulls do not survive the call to prepForForm
          const {
            contactSites = [],
            origin,
            ...contact
          } = action.payload.payload;

          contact.phone = formatPhoneNumber(contact.phone);
          contact.mobile = formatPhoneNumber(contact.mobile);

          contact.effectiveStart =
            contact.effectiveStart?.replace(reDateTime, "$1T$2") ??
            contact.effectiveStart;
          contact.effectiveEnd =
            contact.effectiveEnd?.replace(reDateTime, "$1T$2") ??
            contact.effectiveEnd;
          // Fix our dates because they aren't actually UTC based, so the 'z' at the end is a lie.
          if (origin) {
            origin.phone = formatPhoneNumber(origin.phone);
            origin.mobile = formatPhoneNumber(origin.mobile);

            origin.dateCreated =
              origin.dateCreated?.replace(reDateTime, "$1T$2") ??
              origin.dateCreated;
            origin.dateLastModified =
              origin.dateLastModified?.replace(reDateTime, "$1T$2") ??
              origin.dateLastModified;
          }
          for (let i = 0; i < contactSites?.length; i++) {
            contactSites[i].effectiveStart =
              contactSites[i].effectiveStart?.replace(reDateTime, "$1T$2") ??
              contactSites[i].effectiveStart;
            contactSites[i].effectiveEnd =
              contactSites[i].effectiveEnd?.replace(reDateTime, "$1T$2") ??
              contactSites[i].effectiveEnd;
          }

          state.forms.contact = { ...prepForForm(contact), contactSites }; // remember, we don't want to lose our nulls in contactSites
          state.forms.contactOrigin = prepForForm(
            origin ?? cleanRecord("contactOrigin"),
          );
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error("saveContactEx ERROR action", action);
        }
        completedRequest(state);
      })
      .addCase(saveContactEx.rejected, (state, action) => {
        console.error("saveContactEx.rejected state", state);
        console.error("saveContactEx.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readEventByIdEx.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readEventByIdEx.fulfilled, (state, action) => {
        if (action.payload.rc === "OK") {
          const data = prepForForm(action.payload.payload);
          data.curtailDateStart = data.curtailDateStart?.replace(
            reDateTime,
            "$1T$2",
          );
          data.curtailDateEnd = data.curtailDateEnd?.replace(
            reDateTime,
            "$1T$2",
          );
          if (!data?.eventCommunications) {
            data.eventCommunications = [];
          }
          for (let i = 0; i < data?.eventCommunications?.length; i++) {
            data.eventCommunications[i].schedulePrep = data.eventCommunications[
              i
            ].schedulePrep?.replace(reDateTime, "$1T$2");
            data.eventCommunications[i].scheduleSend = data.eventCommunications[
              i
            ].scheduleSend?.replace(reDateTime, "$1T$2");
          }

          state.forms.event = data;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(readEventByIdEx.rejected, (state, action) => {
        console.error("readEventByIdEx.rejected state", state);
        console.error("readEventByIdEx.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readEventById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readEventById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        data.curtailDateStart = data.curtailDateStart?.replace(
          reDateTime,
          "$1T$2",
        );
        data.curtailDateEnd = data.curtailDateEnd?.replace(reDateTime, "$1T$2");
        state.forms.event = data;
        completedRequest(state);
      })
      .addCase(readEventById.rejected, (state, action) => {
        console.error("readEventById.rejected state", state);
        console.error("readEventById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readEventEmailTypeById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readEventEmailTypeById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        state.forms.eventEmailType = data;
        completedRequest(state);
      })
      .addCase(readEventEmailTypeById.rejected, (state, action) => {
        console.error("readEventEmailTypeById.rejected state", state);
        console.error("readEventEmailTypeById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readEventEmailTemplateById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readEventEmailTemplateById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        state.forms.eventEmailTemplate = data;
        completedRequest(state);
      })
      .addCase(readEventEmailTemplateById.rejected, (state, action) => {
        console.error("readEventEmailTemplateById.rejected state", state);
        console.error("readEventEmailTemplateById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(readProgramById.pending, (state) => {
        pendingRequest(state, LOADING);
      })
      .addCase(readProgramById.fulfilled, (state, action) => {
        const data = prepForForm(action.payload.response);
        state.forms.program = data;
        completedRequest(state);
      })
      .addCase(readProgramById.rejected, (state, action) => {
        console.error("readProgramById.rejected state", state);
        console.error("readProgramById.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteArea.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteArea.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.forms.area = prepForForm(cleanRecord("area"));
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(deleteArea.rejected, (state, action) => {
        console.error("deleteArea.rejected state", state);
        console.error("deleteArea.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteAreaType.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteAreaType.fulfilled, (state, action) => {
        state.forms.areaType = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteAreaType.rejected, (state, action) => {
        console.error("deleteAreaType.rejected state", state);
        console.error("deleteAreaType.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteBaselineMethod.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteBaselineMethod.fulfilled, (state, action) => {
        // So something with state here using the data returned by action
        state.forms.baselineMethod = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteBaselineMethod.rejected, (state, action) => {
        console.error("deleteBaselineMethod.rejected state", state);
        console.error("deleteBaselineMethod.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteContact.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.forms.contact = prepForForm(cleanRecord("contact"));
          state.forms.contactOrigin = prepForForm(cleanRecord("contactOrigin"));
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(deleteContact.rejected, (state, action) => {
        console.error("deleteContact.rejected state", state);
        console.error("deleteContact.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteCurtailPlan.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteCurtailPlan.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.forms.curtailPlan = prepForForm(cleanRecord("curtailPlan"));
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(deleteCurtailPlan.rejected, (state, action) => {
        console.error("deleteCurtailPlan.rejected state", state);
        console.error("deleteCurtailPlan.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteEnrollmentRecord.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteEnrollmentRecord.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.forms.enrollmentRecord = prepForForm(
            cleanRecord("enrollmentRecord"),
          );
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(deleteEnrollmentRecord.rejected, (state, action) => {
        console.error("deleteEnrollmentRecord.rejected state", state);
        console.error("deleteEnrollmentRecord.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteEvent.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        if (action?.payload?.rc === "OK") {
          state.forms.event = prepForForm(cleanRecord("event"));
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
        }
        completedRequest(state);
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        console.error("deleteEvent.rejected state", state);
        console.error("deleteEvent.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteEventEmailType.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteEventEmailType.fulfilled, (state, action) => {
        state.forms.eventEmailType = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteEventEmailType.rejected, (state, action) => {
        console.error("deleteEventEmailType.rejected state", state);
        console.error("deleteEventEmailType.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteEventEmailTemplate.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteEventEmailTemplate.fulfilled, (state, action) => {
        state.forms.eventEmailTemplate = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteEventEmailTemplate.rejected, (state, action) => {
        console.error("deleteEventEmailTemplate.rejected state", state);
        console.error("deleteEventEmailTemplate.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteProgram.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteProgram.fulfilled, (state, action) => {
        state.forms.program = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteProgram.rejected, (state, action) => {
        console.error("deleteProgram.rejected state", state);
        console.error("deleteProgram.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteProgramPrice.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteProgramPrice.fulfilled, (state, action) => {
        state.forms.programPrice = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteProgramPrice.rejected, (state, action) => {
        console.error("deleteProgramPrice.rejected state", state);
        console.error("deleteProgramPrice.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteProgramAdmin.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteProgramAdmin.fulfilled, (state, action) => {
        state.forms.programAdmin = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteProgramAdmin.rejected, (state, action) => {
        console.error("deleteProgramAdmin.rejected state", state);
        console.error("deleteProgramAdmin.rejected action", action);
        rejectedRequest(state);
      })

      .addCase(deleteResource.pending, (state) => {
        pendingRequest(state, SAVING);
      })
      .addCase(deleteResource.fulfilled, (state, action) => {
        state.forms.resource = action.payload.response;
        completedRequest(state);
      })
      .addCase(deleteResource.rejected, (state, action) => {
        console.error("deleteResource.rejected state", state);
        console.error("deleteResource.rejected action", action);
        rejectedRequest(state);
      })

      // These use getFromServer2 and should have the {rc, context, payload} return structure
      .addCase(
        sanityCheckActiveEnrolledBuildingsMissingTimezones.pending,
        (state) => {
          pendingRequest(state, LOADING);
        },
      )
      .addCase(
        sanityCheckActiveEnrolledBuildingsMissingTimezones.fulfilled,
        (state, action) => {
          if (action.payload.rc === "OK") {
            const data = prepForForm(action.payload.payload);
            console.log(data);
          } else {
            console.error("ERROR", action.payload?.context?.sysMessages);
          }
          completedRequest(state);
        },
      )
      .addCase(
        sanityCheckActiveEnrolledBuildingsMissingTimezones.rejected,
        (state, action) => {
          console.error(
            "sanityCheckActiveEnrolledBuildingsMissingTimezones.rejected state",
            state,
          );
          console.error(
            "sanityCheckActiveEnrolledBuildingsMissingTimezones.rejected action",
            action,
          );
          rejectedRequest(state);
        },
      )

      // These use getFromServer2 and should have the {rc, context, payload} return structure
      .addCase(
        sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram.pending,
        (state) => {
          pendingRequest(state, LOADING);
        },
      )
      .addCase(
        sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram.fulfilled,
        (state, action) => {
          if (action.payload.rc === "OK") {
            const data = prepForForm(action.payload.payload);
            console.log(data);
          } else {
            console.error("ERROR", action.payload?.context?.sysMessages);
          }
          completedRequest(state);
        },
      )
      .addCase(
        sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram.rejected,
        (state, action) => {
          console.error(
            "sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram.rejected state",
            state,
          );
          console.error(
            "sanityCheckActiveEnrolledBuildingsMissingTheirAreaParticipantDataForTheProgram.rejected action",
            action,
          );
          rejectedRequest(state);
        },
      )

      // These use getFromServer2 and should have the {rc, context, payload} return structure
      .addCase(
        sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment.pending,
        (state) => {
          pendingRequest(state, LOADING);
        },
      )
      .addCase(
        sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment.fulfilled,
        (state, action) => {
          if (action.payload.rc === "OK") {
            const data = prepForForm(action.payload.payload);
            console.log(data);
          } else {
            console.error("ERROR", action.payload?.context?.sysMessages);
          }
          completedRequest(state);
        },
      )
      .addCase(
        sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment.rejected,
        (state, action) => {
          console.error(
            "sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment.rejected state",
            state,
          );
          console.error(
            "sanityCheckLatestDataTimeFieldsAreTooOldForTheDatapointsUsedByCurtailment.rejected action",
            action,
          );
          rejectedRequest(state);
        },
      )
      .addCase(setEventEmailNotificationOpened.fulfilled, (state, action) => {
        console.log("setEventEmailNotificationOpened.fulfilled state", state);
        console.log("setEventEmailNotificationOpened.fulfilled action", action);
      })
      .addCase(setEventEmailNotificationOpened.rejected, (state, action) => {
        console.error("setEventEmailNotificationOpened.rejected state", state);
        console.error(
          "setEventEmailNotificationOpened.rejected action",
          action,
        );
      })
      .addCase(
        setEventEmailNotificationAcknowledged.fulfilled,
        (state, action) => {
          console.log(
            "setEventEmailNotificationAcknowledged.fulfilled state",
            state,
          );
          console.log(
            "setEventEmailNotificationAcknowledged.fulfilled action",
            action,
          );
        },
      )
      .addCase(
        setEventEmailNotificationAcknowledged.rejected,
        (state, action) => {
          console.error(
            "setEventEmailNotificationAcknowledged.rejected state",
            state,
          );
          console.error(
            "setEventEmailNotificationAcknowledged.rejected action",
            action,
          );
        },
      )
      .addCase(getPreviewInfoByEventIdAndSiteId.fulfilled, (state, action) => {
        //loadEventEmailCommunicationPreview
        console.log("getPreviewInfoByEventIdAndSiteId.fulfilled state", state);
        console.log(
          "getPreviewInfoByEventIdAndSiteId.fulfilled action",
          action,
        );
        if (action?.payload?.rc === "OK") {
          state.eventCommunicationPreviewTokens = action.payload.payload;
          //state.eventCommunicationPreview = action.payload.payload;
          state.eventCommunicationPreview = {
            emailSubject: "",
            emailBody: "",
            smsText: "",
          };
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error(
            "getPreviewInfoByEventIdAndSiteId ERROR action",
            action,
          );
        }
      })
      .addCase(getPreviewInfoByEventIdAndSiteId.rejected, (state, action) => {
        console.error("getPreviewInfoByEventIdAndSiteId.rejected state", state);
        console.error(
          "getPreviewInfoByEventIdAndSiteId.rejected action",
          action,
        );
      })
      .addCase(getEventParticipationByContactId.fulfilled, (state, action) => {
        //getEventParticipationByContactId
        console.log("getEventParticipationByContactId.fulfilled state", state);
        console.log(
          "getEventParticipationByContactId.fulfilled action",
          action,
        );
        if (action?.payload?.rc === "OK") {
          const data = action?.payload?.payload;
          state.participating = data?.today || data.currently;
        } else {
          console.error("ERROR", action.payload?.context?.sysMessages);
          console.error(
            "getEventParticipationByContactId ERROR action",
            action,
          );
        }
      })
      .addCase(getEventParticipationByContactId.rejected, (state, action) => {
        console.error("getEventParticipationByContactId.rejected state", state);
        console.error(
          "getEventParticipationByContactId.rejected action",
          action,
        );
      });
  },
});

export const {
  changeDisplay,
  changeFormField,
  clearForm,
  clearTableData,
  addRevenueShare,
  swapRevenueShare,
  swapContactSite,
  setContactSiteEnabledByIndex,
  setContactSitePropertyByIndex,
  setBuildingSelectionByIndex,
  setBuildingSelectionByCallWindow,
  setBuildingSelectionByAreaId,
  setEnrolledSitesSelected,
  stashEnrolledSites,
  restoreEnrolledSites,
  showSelectedSites,
  clearSelectedSites,
  setTableOpts,
  addEventCommunication,
  swapEventCommunication,
  generateEventEmailCommunicationPreview,
  generateEventTextCommunicationPreview,
  generateEventCommunicationPreview,
  clearEventCommunicationPreview,
} = slice.actions;

export default slice.reducer;
